import React from "react";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { AlertCircle } from "lucide-react";

/**
 * FormField - Reusable validated form field component
 */
export function FormField({ label, htmlFor, error, touched, required, helper, children, className = "" }) {
  const hasError = touched && error;

  return (
    <div className={`space-y-1.5 ${className}`} data-testid={`field-${htmlFor || "unknown"}`}>
      {label && (
        <Label htmlFor={htmlFor} className="text-sm flex items-center gap-1">
          {label}
          {required && <span className="text-red-400 text-xs">*</span>}
        </Label>
      )}
      {children}
      {hasError && (
        <p className="text-xs text-red-500 flex items-center gap-1 mt-1" role="alert" data-testid={`error-${htmlFor || "unknown"}`}>
          <AlertCircle className="w-3 h-3 shrink-0" />{error}
        </p>
      )}
      {helper && !hasError && (
        <p className="text-[11px] text-muted-foreground mt-0.5">{helper}</p>
      )}
    </div>
  );
}

/**
 * ValidatedInput - Input with built-in error styling
 */
export function ValidatedInput({ error, touched, onBlurValidate, className = "", inputRef, ...props }) {
  const hasError = touched && error;
  const errorCls = hasError ? "border-red-500/50 focus:border-red-500 focus:ring-red-500/20" : "";

  return (
    <Input
      ref={inputRef}
      className={`${className} ${errorCls}`}
      aria-invalid={hasError ? "true" : undefined}
      onBlur={(e) => {
        if (onBlurValidate) onBlurValidate(e);
        if (props.onBlur) props.onBlur(e);
      }}
      {...props}
    />
  );
}

import { Button } from "./ui/button";

/**
 * LoadingButton — thin wrapper over the Tadlo <Button> that defaults to the
 * `primary / lg` variant and maps `loading` + `loadingText` into the child.
 * Accepts every <Button> prop (variant, size, icon, fullWidth, etc.).
 */
export function LoadingButton({
  loading,
  loadingText,
  children,
  disabled,
  className = "",
  variant = "primary",
  size = "lg",
  fullWidth,
  ...props
}) {
  return (
    <Button
      loading={loading}
      disabled={disabled}
      variant={variant}
      size={size}
      fullWidth={fullWidth}
      className={className}
      {...props}
    >
      {loading ? (loadingText || "Please wait...") : children}
    </Button>
  );
}

/**
 * FormErrorSummary - Top-of-form error summary
 */
export function FormErrorSummary({ errors, touched }) {
  const visibleErrors = Object.entries(errors).filter(([k]) => touched[k]);
  if (visibleErrors.length === 0) return null;

  return (
    <div className="p-3 rounded-lg border border-red-500/20 bg-red-500/5 mb-4" role="alert" data-testid="form-error-summary">
      <p className="text-xs font-medium text-red-500 flex items-center gap-1.5 mb-1">
        <AlertCircle className="w-3.5 h-3.5" />Please fix the following:
      </p>
      <ul className="text-xs text-red-400 space-y-0.5 pl-5 list-disc">
        {visibleErrors.map(([field, msg]) => <li key={field}>{msg}</li>)}
      </ul>
    </div>
  );
}
